import React, { useState } from 'react';
import { useRituals } from './RitualContext';
import { Trophy, ChevronUp, ChevronDown, Calendar, Gift, Zap, X, Diamond, CheckCircle2, AlertTriangle, LockIcon } from 'lucide-react';
export const RitualTracker: React.FC = () => {
  const {
    todayCompletedCount,
    totalDailyRituals,
    weeklyCompletedCount,
    totalWeeklyRituals,
    streakCount,
    hasUnclaimedRewards,
    pendingLootbox,
    showRitualTracker,
    toggleRitualTracker,
    areAllRequiredRitualsCompleted
  } = useRituals();
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };
  if (!showRitualTracker) return null;
  // Check if all required rituals are completed
  const requiredRitualsCompleted = areAllRequiredRitualsCompleted();
  // Function to navigate to the Ritual Board
  const navigateToRitualBoard = () => {
    // Dispatch an event to navigate to the ritual board
    const event = new CustomEvent('navigate', {
      detail: {
        section: 'rituals'
      }
    });
    document.dispatchEvent(event);
  };
  const handleGoButtonClick = (ritual: any) => {
    // Get the ritual type from the name and description
    const ritualName = (ritual.name || '').toLowerCase();
    const ritualDescription = (ritual.description || '').toLowerCase();
    const ritualText = ritualName + ' ' + ritualDescription;
    console.log('DEBUG: Go button clicked for ritual:', ritualText, ritual.id);
    // DIRECT MATCHES - Check for exact patterns first before any scoring
    // Fantasy scenarios creation - Check this FIRST
    if (ritualText.includes('create') && ritualText.includes('fantasy') && ritualText.includes('scenario') || ritualText.includes('create') && ritualText.includes('fantasy') || ritualText.includes('fantasy scenario') && ritualText.includes('create') || ritualText.includes('create 3 fantasy scenario') || ritualText.includes('create 1 fantasy scenario') || ritualText.match(/create\s+\d+\s+fantasy\s+scenario/)) {
      console.log('DEBUG: Direct match - Fantasy scenario creation (Go button)');
      // Navigate to unleash section
      const navigateEvent = new CustomEvent('navigate', {
        detail: {
          section: 'unleash'
        }
      });
      document.dispatchEvent(navigateEvent);
      // Dispatch specific event to open scenario wizard
      setTimeout(() => {
        const scenarioEvent = new CustomEvent('navigateToScenarioWizard', {
          detail: {
            ritualId: ritual.id,
            ritual: {
              id: ritual.id,
              name: ritualName,
              description: ritualDescription
            }
          }
        });
        document.dispatchEvent(scenarioEvent);
      }, 300);
      return;
    }
    // Hot or Not voting
    if (ritualText.includes('cast') && ritualText.includes('vote') && ritualText.includes('hot or not') || ritualText.includes('hot or not') && ritualText.includes('vote') || ritualText.includes('hotornot') && ritualText.includes('vote') || ritualText.includes('rate') && ritualText.includes('selfie')) {
      console.log('DEBUG: Direct match - Hot or Not voting (Go button)');
      // Dispatch a direct event to navigate to Hot or Not section
      const navigateEvent = new CustomEvent('navigate', {
        detail: {
          section: 'hot_or_not'
        }
      });
      document.dispatchEvent(navigateEvent);
      // Also dispatch a focus event
      setTimeout(() => {
        const focusEvent = new CustomEvent('focusHotOrNotVoting', {
          detail: {
            ritualId: ritual.id
          }
        });
        document.dispatchEvent(focusEvent);
      }, 300);
      return;
    }
    // Fantasy scenarios creation
    if (ritualText.includes('create') && ritualText.includes('fantasy') && ritualText.includes('scenario') || ritualText.includes('create') && ritualText.includes('fantasy') || ritualText.includes('fantasy scenario') && ritualText.includes('create') || ritualText.includes('create 3 fantasy scenario')) {
      console.log('DEBUG: Direct match - Fantasy scenario creation (Go button)');
      // Navigate to unleash section
      const navigateEvent = new CustomEvent('navigate', {
        detail: {
          section: 'unleash'
        }
      });
      document.dispatchEvent(navigateEvent);
      // Dispatch specific event to open scenario wizard
      setTimeout(() => {
        const scenarioEvent = new CustomEvent('navigateToScenarioWizard', {
          detail: {
            ritualId: ritual.id,
            ritual: {
              id: ritual.id,
              name: ritualName,
              description: ritualDescription
            }
          }
        });
        document.dispatchEvent(scenarioEvent);
      }, 300);
      return;
    }
    // Questionnaire completion
    if (ritualText.includes('complete') && ritualText.includes('questionnaire') || ritualText.includes('quick') && ritualText.includes('questionnaire') || ritualText.includes('fill') && ritualText.includes('questionnaire') || ritualText.includes('questionnaire')) {
      console.log('DEBUG: Direct match - Questionnaire completion (Go button)');
      // Navigate to chat section
      const navigateEvent = new CustomEvent('navigate', {
        detail: {
          section: 'chat'
        }
      });
      document.dispatchEvent(navigateEvent);
      // Dispatch event to open questionnaire
      setTimeout(() => {
        const questionnaireEvent = new CustomEvent('openQuestionnaire', {
          detail: {
            ritualId: ritual.id
          }
        });
        document.dispatchEvent(questionnaireEvent);
      }, 300);
      return;
    }
    // Extract key terms from the ritual text to improve classification
    const keyTerms = {
      wheel: ['spin', 'wheel', 'fortune', 'spinthewheelmodal'],
      age: ['verify', 'age', 'identity', 'verification'],
      fantasy: ['fantasy', 'scenario', 'create', 'scenariowizard'],
      deepshift: ['deepshift', 'deep shift', 'deep ai'],
      faceshift: ['faceshift', 'face shift', 'face ai'],
      hot_or_not: ['hot or not', 'hotornot', 'vote', 'rate selfie'],
      questionnaire: ['questionnaire', 'survey', 'form']
    };
    // Determine the primary category based on key terms
    let primaryCategory = '';
    let highestMatchCount = 0;
    for (const [category, terms] of Object.entries(keyTerms)) {
      const matchCount = terms.filter(term => ritualText.includes(term)).length;
      if (matchCount > highestMatchCount) {
        highestMatchCount = matchCount;
        primaryCategory = category;
      }
    }
    // Apply common sense rules
    if (ritualText.includes('create') && ritualText.includes('fantasy')) {
      primaryCategory = 'fantasy';
    }
    if (ritualText.includes('verify') && ritualText.includes('age')) {
      primaryCategory = 'age';
    }
    if (ritualText.includes('spin') && ritualText.includes('wheel')) {
      primaryCategory = 'wheel';
    }
    if (ritualText.includes('hot') && ritualText.includes('not') && ritualText.includes('vote') || ritualText.includes('cast') && ritualText.includes('vote')) {
      primaryCategory = 'hot_or_not';
    }
    if (ritualText.includes('questionnaire')) {
      primaryCategory = 'questionnaire';
    }
    console.log('DEBUG: Determined primary ritual category:', primaryCategory);
    // Dispatch a custom event for the App component to handle
    const goEvent = new CustomEvent('ritualGoButtonClick', {
      detail: {
        ritualId: ritual.id,
        ritualType: ritualText,
        primaryCategory: primaryCategory
      }
    });
    document.dispatchEvent(goEvent);
  };
  return <div className={`fixed bottom-20 left-4 sm:left-24 z-40 flex flex-col items-start transition-all duration-300 ${isExpanded ? 'space-y-2' : ''}`} data-id="element-18417">
      {isExpanded && <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-pink-200 dark:border-pink-900 w-64 overflow-hidden animate-in slide-in-from-left duration-300 mb-2" data-id="element-18418">
          <div className="bg-gradient-to-r from-pink-600 to-purple-600 text-white p-3" data-id="element-18419">
            <div className="flex items-center justify-between" data-id="element-18420">
              <h3 className="font-bold text-sm flex items-center gap-1.5" data-id="element-18421">
                <Trophy size={16} data-id="element-18422" /> Daily Rituals
              </h3>
              <span className="text-xs font-medium bg-white/20 rounded-full px-2 py-0.5" data-id="element-18423">
                {todayCompletedCount}/{totalDailyRituals}
              </span>
            </div>
            <div className="mt-2 h-1.5 bg-white/20 rounded-full overflow-hidden" data-id="element-18424">
              <div className="h-full bg-white rounded-full transition-all duration-500" style={{
            width: `${todayCompletedCount / totalDailyRituals * 100}%`
          }} data-id="element-18425"></div>
            </div>
          </div>
          <div className="p-3 border-b border-gray-200 dark:border-gray-800" data-id="element-18426">
            <div className="flex items-center justify-between mb-1.5" data-id="element-18427">
              <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5" data-id="element-18428">
                <Calendar size={14} data-id="element-18429" /> Weekly Progress
              </h4>
              <span className="text-xs font-medium text-purple-600 dark:text-purple-400" data-id="element-18430">
                {weeklyCompletedCount}/{totalWeeklyRituals}
              </span>
            </div>
            <div className="h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-18431">
              <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-500" style={{
            width: `${weeklyCompletedCount / totalWeeklyRituals * 100}%`
          }} data-id="element-18432"></div>
            </div>
            {pendingLootbox && !requiredRitualsCompleted && <div className="mt-2 flex items-center gap-1.5 text-amber-600 dark:text-amber-400 text-xs" data-id="element-18433">
                <AlertTriangle size={12} data-id="element-18434" />
                <span data-id="element-18435">Premium Ritual Required!</span>
              </div>}
          </div>
          <div className="p-3" data-id="element-18436">
            <div className="flex items-center justify-between mb-2" data-id="element-18437">
              <div className="flex items-center gap-1.5" data-id="element-18438">
                <Zap size={14} className="text-amber-500" data-id="element-18439" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-18440">
                  Current Streak
                </span>
              </div>
              <div className="flex items-center gap-1" data-id="element-18441">
                <span className="text-amber-500" data-id="element-18442">🔥</span>
                <span className="text-xs font-bold text-amber-500" data-id="element-18443">
                  {streakCount} days
                </span>
              </div>
            </div>
            {hasUnclaimedRewards && <button className="w-full py-1.5 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-full text-xs font-medium hover:shadow-md hover:shadow-pink-500/20 transition-all duration-300 flex items-center justify-center gap-1.5" onClick={navigateToRitualBoard} data-id="element-18444">
                <Gift size={14} data-id="element-18445" />
                <span data-id="element-18446">Claim Rewards</span>
              </button>}
            {pendingLootbox && <button className={`mt-2 w-full py-1.5 rounded-full text-xs font-medium hover:shadow-md transition-all duration-300 flex items-center justify-center gap-1.5 ${requiredRitualsCompleted ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-amber-500/20 animate-pulse' : 'bg-gray-200 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`} disabled={!requiredRitualsCompleted} onClick={navigateToRitualBoard} data-id="element-18447">
                {requiredRitualsCompleted ? <>
                    <Gift size={14} data-id="element-18448" />
                    <span data-id="element-18449">Open Weekly Lootbox!</span>
                  </> : <>
                    <LockIcon size={14} data-id="element-18450" />
                    <span data-id="element-18451">Weekly Lootbox Locked</span>
                  </>}
              </button>}
            <button className="mt-2 w-full py-1.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-full text-xs font-medium hover:shadow-md hover:shadow-blue-500/20 transition-all duration-300 flex items-center justify-center gap-1.5" onClick={navigateToRitualBoard} data-id="element-18452">
              <Trophy size={14} data-id="element-18453" />
              <span data-id="element-18454">View All Rituals</span>
            </button>
            {pendingLootbox && !requiredRitualsCompleted && <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-md text-xs text-amber-700 dark:text-amber-400 flex items-start gap-1.5" data-id="element-18455">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" data-id="element-18456" />
                <span data-id="element-18457">
                  You still need to complete your Premium Ritual to unlock this
                  week's lootbox.
                </span>
              </div>}
          </div>
        </div>}
    </div>;
};